/**
 * A mobile scan's tree analysis as the growing-stock report reads it: the `tree` table of its
 * `trees.gpkg` (one point per segment: `genus`, `DBH` in metres, `height`, `allometric_volume`;
 * `genus` is `invalid` for a segment that is not a tree) and, for the map, its `crown` table.
 */
import { useEffect, useRef, useState } from 'react';
import type { Feature, FeatureCollection } from 'geojson';
import { featureAt, inGeometry, insideAny, representativePoint } from '@/map/geometry';
import { MLS_INVALID } from '@/map/layers';
import { ANCHOR } from '@/map/tileFilter';
import type { LinkSource } from '@/map/linkSource';
import { loadLinkedGpkgLayers, type LinkedLayer, type VectorProgress } from '@/map/vectorSource';

export const STEM_TABLE = 'tree';
export const CROWN_TABLE = 'crown';
/** A tree's side view, relative to the analysis's GeoPackage (`<tile>.segmentation/sideviews/<id>.png`). */
export const SIDEVIEW_PROP = 'sideview_path';
/** A tree's stem cross-section at 1.3 m, beside its side view (`<tile>.segmentation/sections/<id>.png`); absent in older analyses. */
export const SECTION_PROP = 'section_path';
/**
 * How far an MLS crown outline may move when it is read (m). The scan's crowns run to thousands of
 * vertices each; at 0.1 m the Kokořínsko scan drops from 4.2 M vertices to 0.12 M with the areas
 * unchanged to a tenth of a percent.
 */
export const CROWN_SIMPLIFY_M = 0.1;

export interface Stem {
  genus: string;
  invalid: boolean;
  dbhCm: number | null;
  height: number | null;
  volume: number | null;
  lng: number;
  lat: number;
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

export function stems(fc: FeatureCollection): Stem[] {
  return fc.features.flatMap((f) => {
    if (f.geometry?.type !== 'Point') return [];
    const [lng, lat] = f.geometry.coordinates;
    if (lng === undefined || lat === undefined) return [];
    const p = f.properties ?? {};
    const genus = typeof p.genus === 'string' && p.genus ? p.genus : 'unknown';
    const dbh = num(p.DBH);
    return [{ genus, invalid: genus === MLS_INVALID, dbhCm: dbh === null ? null : dbh * 100, height: num(p.height), volume: num(p.allometric_volume), lng, lat }];
  });
}

export { featureAt, inGeometry, insideAny };

/** The stems standing inside any of the polygons. */
export function stemsIn(all: Stem[], polygons: Feature[]): Stem[] {
  const inside = insideAny(polygons);
  return all.filter((s) => inside(s.lng, s.lat));
}

/** The point features inside any of the polygons (crowns come as their centroids). */
export function pointsIn(fc: FeatureCollection, polygons: Feature[]): Feature[] {
  const inside = insideAny(polygons);
  return fc.features.filter((f) => {
    const at = anchorOf(f);
    return !!at && inside(at[0], at[1]);
  });
}

/**
 * Where a feature counts: its anchor (`ax`, `ay`, exact, as the archive and the growing stock
 * keep it) when it has one, else its point. Stems decoded from an archive's coarse tiles are a
 * few metres off; the anchor keeps the figures and the map's cut on the same side of a boundary.
 */
export function anchorOf(f: Feature): [number, number] | null {
  const p = f.properties ?? {};
  if (typeof p[ANCHOR.lon] === 'number' && typeof p[ANCHOR.lat] === 'number') return [p[ANCHOR.lon] as number, p[ANCHOR.lat] as number];
  return f.geometry?.type === 'Point' ? [f.geometry.coordinates[0]!, f.geometry.coordinates[1]!] : null;
}

/** The MLS crowns of the given stems, by the tree's id. */
export function crownsOf(crownsFc: FeatureCollection, stemFeatures: Feature[]): Feature[] {
  const trees = new Set(stemFeatures.map((f) => String(f.properties?.id)));
  return crownsFc.features.filter((f) => trees.has(String(f.properties?.tree_id)));
}

export interface GenusRow {
  genus: string;
  count: number;
  volume: number;
}

/** One histogram bar: its label and a count per genus. */
export type HistogramRow = { bin: string } & Record<string, number | string>;

export interface MlsStats {
  count: number;
  valid: { count: number; volume: number };
  invalid: { count: number; volume: number };
  /** The trees by genus, the largest volume first; segments that are no tree are left out. */
  genera: GenusRow[];
  dbh: HistogramRow[];
  height: HistogramRow[];
}

export const DBH_BIN_CM = 10;
export const HEIGHT_BIN_M = 5;

/** Fixed-width bins from 0 up to the largest value, each a stack of counts by genus. */
export function histogram(trees: Stem[], value: (s: Stem) => number | null, width: number, genera: string[]): HistogramRow[] {
  const values = trees.map((s) => [s, value(s)] as const).filter((x): x is readonly [Stem, number] => x[1] !== null && x[1] >= 0);
  if (!values.length) return [];
  // By loop: spreading a large scan's values into Math.max throws a RangeError.
  let top = 0;
  for (const [, v] of values) if (v > top) top = v;
  const bins = Math.floor(top / width) + 1;
  const rows: HistogramRow[] = Array.from({ length: bins }, (_, i) => ({ bin: `${i * width}–${(i + 1) * width}`, ...Object.fromEntries(genera.map((g) => [g, 0])) }));
  for (const [s, v] of values) {
    const row = rows[Math.floor(v / width)]!;
    row[s.genus] = ((row[s.genus] as number | undefined) ?? 0) + 1;
  }
  return rows;
}

export function mlsStats(all: Stem[]): MlsStats {
  const trees = all.filter((s) => !s.invalid);
  const invalid = all.filter((s) => s.invalid);
  const vol = (list: Stem[]) => list.reduce((sum, s) => sum + (s.volume ?? 0), 0);
  const byGenus = new Map<string, Stem[]>();
  for (const s of trees) {
    const list = byGenus.get(s.genus);
    if (list) list.push(s);
    else byGenus.set(s.genus, [s]);
  }
  const genera = [...byGenus.entries()].map(([genus, list]) => ({ genus, count: list.length, volume: vol(list) })).sort((a, b) => b.volume - a.volume || a.genus.localeCompare(b.genus));
  const keys = genera.map((g) => g.genus);
  return {
    count: all.length,
    valid: { count: trees.length, volume: vol(trees) },
    invalid: { count: invalid.length, volume: vol(invalid) },
    genera,
    dbh: histogram(trees, (s) => s.dbhCm, DBH_BIN_CM, keys),
    height: histogram(trees, (s) => s.height, HEIGHT_BIN_M, keys),
  };
}

export interface MlsData {
  stems: FeatureCollection | null;
  crowns: FeatureCollection | null;
  loading: boolean;
  failed: boolean;
}

/**
 * The scan's stems, and its crowns when the map draws them; nothing when no scan is bound.
 * `file` is the analysis's own GeoPackage (its name comes with the report's grant) and its
 * digest, which keys the browser's cache: a page opened again reads the file from there.
 */
export function useMlsData(file: LinkedLayer | undefined, links: LinkSource, withCrowns: boolean, onProgress?: (progress: VectorProgress | null) => void): MlsData {
  const [state, setState] = useState<MlsData>({ stems: null, crowns: null, loading: !!file, failed: false });
  const key = file ? JSON.stringify([file.object, file.path, file.digest]) : '';
  const progressRef = useRef(onProgress);
  useEffect(() => {
    progressRef.current = onProgress;
  }, [onProgress]);
  useEffect(() => {
    if (!key) return;
    const [object, path, digest] = JSON.parse(key) as [string, string, string];
    let alive = true;
    (path ? loadLinkedGpkgLayers({ object, path, digest }, links, withCrowns ? [STEM_TABLE, CROWN_TABLE] : [STEM_TABLE], { simplifyM: { [CROWN_TABLE]: CROWN_SIMPLIFY_M }, onProgress: (p) => alive && progressRef.current?.(p) }) : Promise.reject(new Error('no GeoPackage')))
      .then(
        (t) => alive && setState({ stems: t[STEM_TABLE] ?? null, crowns: t[CROWN_TABLE] ?? null, loading: false, failed: !t[STEM_TABLE] }),
        () => alive && setState({ stems: null, crowns: null, loading: false, failed: true }),
      )
      .finally(() => alive && progressRef.current?.(null));
    return () => {
      alive = false;
    };
  }, [key, links, withCrowns]);
  return state;
}

export { representativePoint };

/** An MLS stem and its crown by the tree's id (`tree.id`, `crown.tree_id`). */
export interface MlsIndex {
  stems: Map<string, Feature>;
  crowns: Map<string, Feature>;
}

export function mlsIndex(stemsFc: FeatureCollection | null, crownsFc: FeatureCollection | null): MlsIndex {
  const key = (v: unknown) => (v === undefined || v === null ? null : String(v));
  const index = (fc: FeatureCollection | null, prop: string) => new Map((fc?.features ?? []).flatMap((f) => (key(f.properties?.[prop]) === null ? [] : [[key(f.properties?.[prop])!, f] as const])));
  return { stems: index(stemsFc, 'id'), crowns: index(crownsFc, 'tree_id') };
}

/**
 * The counterpart of a clicked MLS feature — a crown's stem, a stem's crown — with the line
 * from the clicked one to it; null when the other is not there or has no id to highlight.
 */
export function mlsRelated(index: MlsIndex, clicked: 'stem' | 'crown', properties: Record<string, unknown>): { feature: Feature; line: [[number, number], [number, number]] } | null {
  const tree = String(properties[clicked === 'stem' ? 'id' : 'tree_id'] ?? '');
  const stem = index.stems.get(tree);
  const crown = index.crowns.get(tree);
  const from = stem && representativePoint(stem.geometry);
  const to = crown && representativePoint(crown.geometry);
  if (!stem || !crown || !from || !to) return null;
  const other = clicked === 'stem' ? crown : stem;
  if (other.id === undefined) return null;
  return { feature: other, line: clicked === 'stem' ? [from, to] : [to, from] };
}
